import * as React from 'react';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

// Module-level variable to avoid React lifecycle issues
let globalClearingFlag = false;

interface SearchInputProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  onSearchExecuted?: (query: string) => void;
  value?: string;
  className?: string;
  isLoading?: boolean;
  maintainFocus?: boolean;
  type?: 'search-header' | 'search-results';
  // Event triggering props
  enableSearchEvents?: boolean;
  searchEventsToTrigger?: string;
  eventId?: string; // Add this for group block compatibility
  settings: {
    block_settings: {
      always_show?: boolean;
      input_placeholder?: string;
      character_threshold?: number;
      [key: string]: any;
    }
  }
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(({ 
  settings, 
  placeholder = 'Search',
  onSearch,
  onSearchExecuted,
  value: initialValue = '',
  className = '',
  isLoading = false,
  maintainFocus = false,
  type = 'search-header',
  searchEventsToTrigger,
  enableSearchEvents = true,
  eventId,
  ...props 
}, ref) => {
  const [query, setQuery] = useState(initialValue);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Use module-level variable instead of ref to avoid React lifecycle issues
  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();

  // Extract settings for easier access
  const blockSettings = settings?.settings?.block_settings || {};
  const alwaysShow = blockSettings.always_show || false;
  const characterThreshold = blockSettings.character_threshold || 1;
  const inputPlaceholder = blockSettings.input_placeholder || placeholder;

  const debounceTimeout = useRef(null);

  useImperativeHandle(ref, () => inputRef.current!);

  const handleInputChange = useCallback((e) => {
    const value = e?.target?.value || '';
    setQuery(value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (value.length >= characterThreshold) {
        // Dispatch search event for provider to pick up
        window.dispatchEvent(
          new CustomEvent('searchrequest', {
            detail: {
              query: value,
              type: 'header-search',
              limit: 4,
              settings,
            },
          })
        );

         window.dispatchEvent(new CustomEvent('search-show'));
      } else if (value.length === 0 && !globalClearingFlag) {
        // Dispatch search cleared event
        window.dispatchEvent(
          new CustomEvent('search-cleared', {
            detail: {
              type: 'search',
              settings,
            },
          })
        );
      }
    }, 250); // 250ms debounce
  }, [settings, characterThreshold]);

  const clearSearch = (event) => {
      event.stopPropagation();
      event.preventDefault();

      globalClearingFlag = true;
      setQuery('');
      if (onSearch) {
        onSearch('');
      }
      if (onSearchExecuted) {
        onSearchExecuted('');
      }

      // Maintain focus after clearing if required
      if (maintainFocus && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      }

      // Always reset the clearing flag after a fixed delay to catch all events
      setTimeout(() => {
        globalClearingFlag = false;
      }, 1000);
  };

  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    window.dispatchEvent(new CustomEvent('search-show'));
    handleInputChange(event);
  }, []);

  const checkOpenState = useCallback((event) => {
      const { eventId, state } = event.detail || {};
      const eventType = event.type;

      // Ignore search close events when we're in the middle of clearing
      if (globalClearingFlag && ((eventId === 'header-search-hide') || (eventId === 'search' && state === 'hidden'))) {
        return;
      }
      
      if (eventId !== 'header-search-show' && eventId !== 'search' && eventId !== 'header-search-hide' && eventType !== 'boost-sd-close-isw') return;
      if (eventId === 'search' && state === 'hidden') {
        setIsOpen(false);
        document.body.classList.remove('no-scroll');
      }
      if (eventId === 'search' && state === 'visible') {
        document.body.classList.add('no-scroll');
      }
      if (eventId === 'header-search-show') {
        return setIsOpen(true);
      }
      if (eventId === 'header-search-hide' || eventType === 'boost-sd-close-isw') {
        window.dispatchEvent(new CustomEvent('search-open-show'));
        return setIsOpen(false);
      }
  });

  useEffect(() => {
    window.addEventListener('header-search-show', checkOpenState);
    window.addEventListener('header-search-hide', checkOpenState);
    window.addEventListener('boost-sd-close-isw', checkOpenState);
    return () => {
      window.removeEventListener('block-builder-state-change', checkOpenState);
      window.removeEventListener('header-search-show', checkOpenState);
      window.removeEventListener('header-search-hide', checkOpenState);
      window.removeEventListener('boost-sd-close-isw', checkOpenState);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.querySelectorAll('.hide-when-search-closed').forEach((el) => el.classList.remove('hidden'));
      document.querySelectorAll('.hide-when-search-open').forEach((el) => el.classList.add('hidden'));
      document.body.classList.add('no-scroll');
    } else {
      document.querySelectorAll('.hide-when-search-closed').forEach((el) => el.classList.add('hidden'));
      document.querySelectorAll('.hide-when-search-open').forEach((el) => el.classList.remove('hidden'));
      document.body.classList.remove('no-scroll');
    }
  }, [isOpen]);

  const shouldShowInput = alwaysShow || isOpen;

  return (
      <form
        action="/search"
        className={`relative w-full ${className} ${shouldShowInput ? 'visible' : 'invisible'}`}
      >
        <input type="hidden" name="type" value="product" />
        <div className='relative flex items-center'>
          <i className='icon icon-search absolute left-5 text-[15px] font-bold' />

          <input
            name="q"
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={inputPlaceholder}
            className={`w-full pl-10 pr-3 py-[10px] focus:bg-[#ECEFF1] border-2 border-[#3E4244] rounded-[10px] ${
              isMobile || isTablet ? 'l2' : 'l3'
            }`}
          />

          {isLoading && (
            <div className='absolute right-4 w-5 h-5'>
              <svg
                className='animate-spin h-5 w-5 text-gray-400'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                ></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            </div>
          )}

          <button
            type='button'
            onClick={clearSearch}
            className={`absolute w-5 h-5 rounded-full bg-black right-3 ${query && !isLoading ? 'block' : 'hidden'}`}
          >
            <span className={`text-white font-bold icon-close`}></span>
          </button>
        </div>
      </form>
    );
  }
);
